import { useEffect, useMemo, useState } from "react";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { Trash2, X } from "lucide-react";
import { TeamRole } from "../../types";
import {
  Flyin,
  FlyinBody,
  FlyinFooter,
  FlyinHeader,
  PaginationControls,
  FlyinTitle,
} from "../../../../shared/ui";
import { formatPermissionLabel } from "../../../../shared/lib";
import { Edit2, More, UserRemove } from "iconsax-reactjs";

type RoleDetailsFlyinProps = {
  open: boolean;
  role: TeamRole | null;
  showAllPermissions: boolean;
  onOpenChange: (open: boolean) => void;
  onToggleShowAllPermissions: () => void;
  onAssignMembers: (role: TeamRole) => void;
  onEditRole: (role: TeamRole) => void;
  onDeleteRole: (role: TeamRole) => void;
  onRemoveMemberFromRole: (memberId: string) => void;
};

const flyinPermissionsPreviewCount = 1;

const RoleDetailsFlyin = ({
  open,
  role,
  showAllPermissions,
  onOpenChange,
  onToggleShowAllPermissions,
  onAssignMembers,
  onEditRole,
  onDeleteRole,
  onRemoveMemberFromRole,
}: RoleDetailsFlyinProps) => {
  const [membersPage, setMembersPage] = useState(1);
  const membersPerPage = 5;

  const flyinPermissions = role?.permissions ?? [];
  const visibleFlyinPermissions = showAllPermissions
    ? flyinPermissions
    : flyinPermissions.slice(0, flyinPermissionsPreviewCount);
  const flyinRemainingPermissions = Math.max(
    flyinPermissions.length - flyinPermissionsPreviewCount,
    0,
  );
  const roleMembers = useMemo(() => role?.members ?? [], [role?.members]);
  const totalMemberPages = Math.max(
    1,
    Math.ceil(roleMembers.length / membersPerPage),
  );
  const pagedRoleMembers = useMemo(() => {
    const start = (membersPage - 1) * membersPerPage;
    return roleMembers.slice(start, start + membersPerPage);
  }, [membersPage, roleMembers]);

  useEffect(() => {
    if (!open) {
      setMembersPage(1);
    }
  }, [open]);

  useEffect(() => {
    setMembersPage((prev) => Math.min(prev, totalMemberPages));
  }, [totalMemberPages]);

  return (
    <Flyin open={open} onOpenChange={onOpenChange}>
      <FlyinHeader>
        <div>
          <FlyinTitle className="text-[24px] font-[600] text-[#1F2937]">
            {role?.name ?? "Role"}
          </FlyinTitle>
          <p className="mt-2 text-[16px] text-[#161827] font-[400]">
            {role?.description ??
              "Role details will appear here once selected."}
          </p>
        </div>
        <button
          type="button"
          aria-label="Close panel"
          className="rounded-full p-2 text-[#737AA8] hover:bg-[#F2F4FF]"
          onClick={() => onOpenChange(false)}
        >
          <X size={20} />
        </button>
      </FlyinHeader>
      <FlyinBody className="space-y-6">
        <section>
          <div className="flex items-center justify-between">
            <h3 className="text-[14px] font-[450] text-[#161827]">
              Permissions
            </h3>
            {flyinPermissions.length > flyinPermissionsPreviewCount ? (
              <button
                type="button"
                className="text-[12px] font-[450] text-[#161827]"
                onClick={onToggleShowAllPermissions}
              >
                {showAllPermissions ? "Show less" : "Show all"}
              </button>
            ) : null}
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            {visibleFlyinPermissions.map((permission) => (
              <span
                key={permission}
                className="rounded-full bg-[#F7F8FD] px-4 py-1 text-[14px] font-[400] text-[#161827]"
              >
                {formatPermissionLabel(permission)}
              </span>
            ))}
            {!showAllPermissions && flyinRemainingPermissions > 0 ? (
              <span className="rounded-full bg-[#F7F8FD] px-4 py-1 text-[14px] font-[400] text-[#161827]">
                +{flyinRemainingPermissions} more
              </span>
            ) : null}
          </div>
        </section>

        <section>
          <div className="flex items-center justify-between">
            <h3 className="text-[14px] font-[500] text-[#161827]">Members</h3>
            <button
              type="button"
              className="text-[12px] font-[500] text-[#161827]"
              onClick={() => {
                if (!role) return;
                onAssignMembers(role);
                onOpenChange(false);
              }}
            >
              Assign
            </button>
          </div>
          <div className="mt-3 rounded-[16px] border border-[#E4E7F4] bg-white p-2">
            {pagedRoleMembers.map((member) => (
              <div
                key={`${role?.id}-${member.id}`}
                className="flex items-center justify-between rounded-[12px] px-3 py-3 text-[14px] text-[#2C2F44] hover:bg-[#F7F8FD]"
              >
                <div className="flex items-center gap-3">
                  <div className="flex h-9 w-9 items-center justify-center rounded-full bg-[#EEF1FB] text-[13px] font-[600] text-[#3B4060]">
                    {member.label}
                  </div>
                  <span className="text-[14px] font-[450]">{member.name}</span>
                </div>
                <DropdownMenu.Root>
                  <DropdownMenu.Trigger asChild>
                    <button
                      type="button"
                      className="rounded-md p-1 text-[#6E7397] hover:bg-[#F3F4FF]"
                      aria-label={`More actions for ${member.name}`}
                      onClick={(event) => event.stopPropagation()}
                    >
                      <More size={18} color="#6E7397" />
                    </button>
                  </DropdownMenu.Trigger>
                  <DropdownMenu.Portal>
                    <DropdownMenu.Content
                      align="end"
                      sideOffset={10}
                      className="z-[120] min-w-[320px] rounded-[20px] border border-[#E1E6FF] bg-white p-2 shadow-[0_20px_45px_rgba(15,20,40,0.15)]"
                    >
                      <DropdownMenu.Item
                        onSelect={(event) => {
                          event.stopPropagation();
                          onRemoveMemberFromRole(member.id);
                        }}
                        className="flex cursor-pointer items-center gap-3 rounded-[14px] px-3 py-3 text-[16px] font-[500] text-[#161827] outline-none transition-colors focus:bg-[#F5F6FD]"
                      >
                        <UserRemove size={20} className="text-[#6E7397]" />
                        Remove from role
                      </DropdownMenu.Item>
                    </DropdownMenu.Content>
                  </DropdownMenu.Portal>
                </DropdownMenu.Root>
              </div>
            ))}

            <div className="pl-2 pb-2">
              {roleMembers.length > membersPerPage ? (
                <PaginationControls
                  currentPage={membersPage}
                  totalPages={totalMemberPages}
                  onPageChange={setMembersPage}
                  className="mt-3 flex items-center gap-2"
                />
              ) : null}
            </div>
          </div>
        </section>
      </FlyinBody>
      <FlyinFooter className="gap-10">
        <button
          type="button"
          className="flex items-center gap-2 text-[14px] font-[500] text-[#5B21FF]"
          onClick={() => {
            if (!role) return;
            onEditRole(role);
            onOpenChange(false);
          }}
        >
          <Edit2 size={18} />
          Edit role
        </button>
        <button
          type="button"
          className="flex items-center gap-2 text-[14px] font-[500] text-[#E24B4B]"
          onClick={() => {
            if (!role) return;
            onDeleteRole(role);
            onOpenChange(false);
          }}
        >
          <Trash2 size={18} />
          Delete role
        </button>
      </FlyinFooter>
    </Flyin>
  );
};

export default RoleDetailsFlyin;
